import React, { useMemo } from "react";
import { ViewDensity } from "@common/types/views/views";
import { WorkspaceAction } from "@common/userWorkspaceRoles";
import {
    useCategoryCoaMappings,
    useUpdateCategoryCoaMapping,
} from "@api/categoryCoaMapping.api";
import { useEntity } from "@components/entity/useEntity";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { SectionHeader } from "@design-system/Section/Section";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { useCanPerformWorkspaceAction } from "@hooks/permissions/useCanPerformWorkspaceAction";
import { useAccounts } from "@hooks/useAccounts";
import { ViewConfig } from "@lib/views";
import { CategoryAccountCell } from "./cells/CategoryAccountCell";
import { CategoryPathCell } from "./cells/CategoryPathCell";
import type {
    CategorizationRow,
    CategorizationTableMeta,
} from "./categorizationTableMeta";
import styles from "./CategorizationSettings.module.scss";

enum CategorizationColumn {
    CATEGORY = "category",
    ACCOUNT = "account",
}

const viewConfig: ViewConfig<CategorizationRow, CategorizationColumn, never> = {
    columns: {
        [CategorizationColumn.CATEGORY]: {
            accessorKey: "categoryPath",
            name: "Category",
            header: "Category",
            immutable: true,
            minSize: 200,
            maxSize: 1200,
            cell: CategoryPathCell,
        },
        [CategorizationColumn.ACCOUNT]: {
            accessorKey: "accountId",
            name: "Account",
            header: "Account",
            immutable: true,
            minSize: 240,
            maxSize: 1200,
            cell: CategoryAccountCell,
        },
    },
};

const view = {
    filters: {},
    columns: [
        { id: CategorizationColumn.CATEGORY },
        { id: CategorizationColumn.ACCOUNT },
    ],
    options: {
        density: ViewDensity.MD,
    },
};

export const CategorizationSettings: React.FC = () => {
    const entity = useEntity();
    const { data, isLoading, isError } = useCategoryCoaMappings(entity.id);
    const { accountsById } = useAccounts({
        entityId: entity.id,
        includeDisabled: true,
    });
    const { mutate: updateMapping } = useUpdateCategoryCoaMapping(entity.id);
    const canEditMappings = useCanPerformWorkspaceAction(
        WorkspaceAction.COA_EDIT,
    );

    const rows: CategorizationRow[] | undefined = useMemo(
        () =>
            data?.mappings.map((mapping) => ({
                ...mapping,
                id: mapping.taxonomy,
            })),
        [data],
    );
    const assignableAccountIds = useMemo(
        () => new Set(data?.assignableAccountIds),
        [data],
    );

    const tableMeta: CategorizationTableMeta = useMemo(
        () => ({
            options: {
                entityId: entity.id,
                accountsById,
                assignableAccountIds,
                canEditMappings,
            },
            callbacks: {
                onSelectAccount: ({ taxonomy, accountId }) => {
                    updateMapping({ taxonomy, accountId });
                },
            },
        }),
        [
            entity.id,
            accountsById,
            assignableAccountIds,
            canEditMappings,
            updateMapping,
        ],
    );

    return (
        <section className={styles.section}>
            <SectionHeader>
                <h4>Categorization</h4>
            </SectionHeader>
            <p className={styles.description}>
                Kick categories map to these accounts when transactions are
                categorized. Changes apply to new categorizations.
            </p>

            {isLoading && <Loader />}
            {isError && (
                <EmptyState
                    header="Failed to load category mappings"
                    body="Try again in a moment."
                />
            )}
            {rows && (
                <div className={styles.tableContainer}>
                    <ViewTable
                        data={rows}
                        view={view}
                        config={viewConfig}
                        rowBorders
                        columnBorders
                        enableColumnResizing={false}
                        tableMeta={tableMeta}
                        emptyView={() => (
                            <span>No categories to map for this entity.</span>
                        )}
                    />
                </div>
            )}
        </section>
    );
};
